/**
 * @file
 * Card, button and icon hover/click colour-swap behaviors for layerone.
 */
(function ($, Drupal) {

  'use strict';

  // A real, directly-usable CSS colour value (hex, rgb()/hsl()/var()/
  // linear-gradient()) rather than one of this theme's legacy class-name
  // colour tokens (e.g. "bg-jet-black"). Fields/templates still on the
  // legacy tokens keep swapping classes exactly as before; a real value
  // swaps via inline style instead, since toggling it as a class would be
  // invalid CSS (e.g. class="#000000").
  function isCssValue(value) {
    if (!value) {
      return false;
    }
    return /^#[0-9a-fA-F]{3,8}$/.test(value) || /^(rgb|rgba|hsl|hsla|var|linear-gradient)\(/i.test(value);
  }

  // Swaps one colour role from `fromValue` to `toValue` on `el`. Which
  // mechanism applies is decided by `toValue` alone (the value actually
  // being applied) — a field can be mid-migration, with its default value
  // already a real CSS value but its hover counterpart still a legacy
  // token (or vice versa), so `fromValue`'s format doesn't tell us
  // anything about how to apply `toValue`. Whichever mechanism *isn't*
  // being used this time is explicitly cleared, so a leftover inline
  // style can't visually fight a legacy class the next time this swaps
  // back (legacy utility classes use !important, so this is mostly
  // belt-and-braces, but keeps state from silently accumulating).
  function swapColour(el, cssProperty, fromValue, toValue) {
    if (isCssValue(toValue)) {
      if (!isCssValue(fromValue)) {
        el.removeClass(fromValue);
      }
      // 'color' needs !important: _general.scss's global `a { color:
      // var(--link-color) !important; }` outranks jQuery's .css() (a
      // plain inline style) regardless of specificity, the same way it
      // outranks the server-rendered inline style this mirrors
      // (paragraph--button--default.html.twig) - jQuery's .css() has no
      // !important option, so this goes through the native property
      // instead. Other properties (background-color, icon filters) don't
      // fight an !important rule like this, so they're untouched.
      if (cssProperty === 'color') {
        el.get(0).style.setProperty('color', toValue, 'important');
      }
      else {
        el.css(cssProperty, toValue);
      }
    }
    else {
      if (isCssValue(fromValue)) {
        el.css(cssProperty, '');
      }
      el.removeClass(fromValue).addClass(toValue);
    }
  }

  // Border colour is a special case: a "border-lr" side selection needs
  // both border-left-color AND border-right-color set — there's no single
  // shorthand for "left and right but not top or bottom" — so a real CSS
  // value can't be applied with one swapColour() call the way background/
  // text/icon colour can. Templates that have a side selector (e.g.
  // checklist_item, via field_borders) precompute the exact property list
  // for the current side with layerone_colour_render's
  // border_colour_style() Twig function, and pass the result through as
  // data-border-style-default/data-border-style-hover — a ready-to-apply
  // "property: value;..." fragment, or empty when the side's colour is
  // still a legacy class-name token. Bundles without a side selector
  // (e.g. button, always plain 'border') never set those attributes, so
  // this always falls through to the plain single-property swap for them.
  function applyStyleFragment(el, fragment) {
    fragment.split(';').forEach(function (declaration) {
      var parts = declaration.split(':');
      if (parts.length === 2 && parts[0].trim() !== '') {
        el.css(parts[0].trim(), parts[1].trim());
      }
    });
  }

  function clearBorderColourStyles(el) {
    ['border-color', 'border-top-color', 'border-bottom-color', 'border-left-color', 'border-right-color', '--border-colour'].forEach(function (property) {
      el.css(property, '');
    });
  }

  function swapBorderColour(el, fromClass, toClass, toStyleFragment) {
    clearBorderColourStyles(el);
    if (toStyleFragment) {
      if (fromClass) {
        el.removeClass(fromClass);
      }
      applyStyleFragment(el, toStyleFragment);
    }
    else if (isCssValue(toClass)) {
      // A real CSS colour value (e.g. "#ffffff") with no precomputed
      // side fragment (data-border-style-*). Apply it inline, the way
      // swapColour() does for background/text — addClass("#ffffff") is an
      // invalid, no-op class, which is why border-color silently stayed
      // unchanged on hover for these while background and text (which go
      // through swapColour) did update.
      //
      // WHICH sides get recoloured is read from what's actually
      // rendered: any side whose computed border-width is non-zero is a
      // real, visible border (however it got there — a border-left /
      // border-lr / border-tb class on the element, an inline width, a
      // parent rule) and is recoloured; sides with no border are left
      // alone. This is the general form of what border_colour_style()
      // precomputes server-side for bundles that have a field_borders
      // side selector — done here from the DOM so it also covers bundles
      // that don't pass a fragment. Falls back to the border-color
      // shorthand when nothing has a measurable border.
      if (!isCssValue(fromClass)) {
        el.removeClass(fromClass);
      }
      var sideColourProperty = {
        'border-top-width': 'border-top-color',
        'border-right-width': 'border-right-color',
        'border-bottom-width': 'border-bottom-color',
        'border-left-width': 'border-left-color'
      };
      var recolouredASide = false;
      Object.keys(sideColourProperty).forEach(function (widthProperty) {
        if (parseFloat(el.css(widthProperty)) > 0) {
          el.css(sideColourProperty[widthProperty], toClass);
          recolouredASide = true;
        }
      });
      if (!recolouredASide) {
        el.css('border-color', toClass);
      }
      // Bootstrap's .border (all sides) sets its colour with !important:
      // _borders.scss applies --border-colour over it.
      if (el.hasClass('border')) {
        el.css('--border-colour', toClass);
      }
    }
    else {
      el.removeClass(fromClass).addClass(toClass);
    }
  }

  Drupal.behaviors.layeroneHoverEffects = {
    attach: function (context, settings) {

      // Card hovers: in / out, shared with the List item buttons below.
      const cardHoverIn = function () {
          let background = $(this).data('background');
          let backgroundHover = $(this).data('background-hover');
          let text = $(this).data('text');
          let textHover = $(this).data('text-hover');
          let border = $(this).data('border');
          let borderHover = $(this).data('border-hover');
          let borderStyleHover = $(this).data('border-style-hover');
          let shadow = $(this).data('shadow');
          if (shadow !== 'undefined') {
            $(this).addClass(shadow);
          }
          swapColour($(this), 'background-color', background, backgroundHover);
          swapColour($(this), 'color', text, textHover);
          swapBorderColour($(this), border, borderHover, borderStyleHover);
          $(this).find('.icon').each(function() {
            let iconColour = $(this).data('filter');
            let iconHoverColour = $(this).data('filter-hover');
            swapColour($(this), 'background-color', iconColour, iconHoverColour);
          });
          $(this).find('.icon-background').each(function() {
            let iconBackground = $(this).data('background');
            let iconBackgroundHover = $(this).data('background-hover');
            swapColour($(this), 'background-color', iconBackground, iconBackgroundHover);
          });
      };
      const cardHoverOut = function () {
          let background = $(this).data('background');
          let backgroundHover = $(this).data('background-hover');
          let text = $(this).data('text');
          let textHover = $(this).data('text-hover');
          let border = $(this).data('border');
          let borderHover = $(this).data('border-hover');
          let borderStyleDefault = $(this).data('border-style-default');
          let shadow = $(this).data('shadow');
          if (shadow !== 'undefined') {
            $(this).removeClass(shadow);
          }
          swapColour($(this), 'background-color', backgroundHover, background);
          swapColour($(this), 'color', textHover, text);
          swapBorderColour($(this), borderHover, border, borderStyleDefault);
          $(this).find('.icon').each(function() {
            let iconColour = $(this).data('filter');
            let iconHoverColour = $(this).data('filter-hover');
            swapColour($(this), 'background-color', iconHoverColour, iconColour);
          });
          $(this).find('.icon-background').each(function() {
            let iconBackground = $(this).data('background');
            let iconBackgroundHover = $(this).data('background-hover');
            swapColour($(this), 'background-color', iconBackgroundHover, iconBackground);
          });
      };
      // span.btn--item-link is a button inside a List item that's already
      // one big link (paragraph--button--default.html.twig): it shows its
      // hover colours while the item is hovered - anywhere on the card, the
      // click goes to the same place - rather than only while the pointer
      // is on the button itself.
      $(".card, .btn:not(.btn--item-link), .checklist-item, .linkable-icon", context).hover(cardHoverIn, cardHoverOut);
      $(".checklist-item", context).each(function () {
        const item = this;
        const $buttons = $(item).find('.btn--item-link').filter(function () {
          return $(this).closest('.checklist-item')[0] === item;
        });
        if ($buttons.length) {
          $(item).on('mouseenter', function () { $buttons.each(cardHoverIn); });
          $(item).on('mouseleave', function () { $buttons.each(cardHoverOut); });
        }
      });

      $( ".search-card", context).hover(
        function() {
          let background = $(this).data('background');
          let backgroundHover = $(this).data('background-hover');
          let text = $(this).data('text');
          let textHover = $(this).data('text-hover');
          let border = $(this).data('border');
          let borderHover = $(this).data('border-hover');
          let pill = $(this).data('pill-colour');
          let pillHover = $(this).data('pill-hover-colour');
          $(this).removeClass(background).addClass(backgroundHover);
          $(this).removeClass(text).addClass(textHover);
          $(this).removeClass(border).addClass(borderHover);
          $(this).find('.tag').each(function() {
            $(this).removeClass(pill).removeClass(text).addClass(pillHover).addClass(textHover);
          });
        }, function() {
          let background = $(this).data('background');
          let backgroundHover = $(this).data('background-hover');
          let text = $(this).data('text');
          let textHover = $(this).data('text-hover');
          let border = $(this).data('border');
          let borderHover = $(this).data('border-hover');
          let pill = $(this).data('pill-colour');
          let pillHover = $(this).data('pill-hover-colour');
          $(this).removeClass(backgroundHover).addClass(background);
          $(this).removeClass(textHover).addClass(text);
          $(this).removeClass(borderHover).addClass(border);
          $(this).find('.tag').each(function() {
            $(this).removeClass(pillHover).removeClass(textHover).addClass(pill).addClass(text);
          });
        }
      );

      // SVG Filters
      /*
      $('.paragraph-button', context).hover(function(e) {
        let colour = $(this).data('colour');
        let background = $(this).data('background');
        let hover = $(this).data('hover');
        e.stopPropagation();
        $(this).removeClass('text-' + background).addClass('text-' + colour).removeClass('bg-' + colour).addClass('bg-' + hover);
        $(this).find('.icon').removeClass('filter-' + background).addClass('filter-' + colour);
      }, function(){
        let colour = $(this).data('text');
        let background = $(this).data('background');
        let hover = $(this).data('hover');
        $(this).removeClass('text-' + colour).addClass('text-' + background).removeClass('bg-' + hover).addClass('bg-' + colour);
        $(this).find('.icon').removeClass('filter-' + colour).addClass('filter-' + background);
      });

       */

      $('.btn-tertiary', context).hover(function() {
        let iconHoverColour = $(this).data('filter-hover');
        let iconColour = $(this).data('filter');
        $(this).removeClass(iconColour);
        if (!$(this).hasClass(iconHoverColour)) {
          $(this).addClass(iconHoverColour);
        }
      });

      $('.btn-primary-gold').hover(
        function() {
        let icon = $(this).find('.icon');
        if (icon.hasClass('icon-chevron-left-large-dark-navy')) {
          icon.addClass('icon-chevron-left-large-white').removeClass('icon-chevron-left-large-dark-navy');
        }
      },
        function() {
          let icon = $(this).find('.icon');
          if (icon.hasClass('icon-chevron-left-large-white')) {
            icon.addClass('icon-chevron-left-large-dark-navy').removeClass('icon-chevron-left-large-white');
          }
        });

      $('div.card', context).click(function() {
        let href = $(this).find('a').attr('href');
        window.location.href = href;
      });

      $('div.card').hover(function() {
        let link = $(this).find('a');
        let text = link.data('text');
        let textHover = link.data('text-hover');
        let background = link.data('background');
        let backgroundHover = link.data('background-hover');
        let border = link.data('border');
        let borderHover = link.data('border-hover');
        link.removeClass(text).addClass(textHover).removeClass(background).removeClass(border).addClass(borderHover).addClass(backgroundHover);

      }, function() {
        let link = $(this).find('a');
        let text = link.data('text');
        let textHover = link.data('text-hover');
        let background = link.data('background');
        let backgroundHover = link.data('background-hover');
        let border = link.data('border');
        let borderHover = link.data('border-hover');
        link.removeClass(textHover).addClass(text).removeClass(backgroundHover).addClass(background).removeClass(borderHover).addClass(border);
      });

    }
  };

})(jQuery, Drupal);
